import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { PERMISSION_FREE_MODULE, permissionModuleName } from "@apv-erp/status";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { usePermissionModule } from "@/hooks/usePermissionModule";

/**
 * Quyền In / Xuất DL / Nhập DL của một module (04/11/2026), đọc 100% từ bảng phân quyền.
 * Không truyền module → module của màn đang mở (theo menu). Màn không thuộc module nào → chỉ Admin / quyền '*'.
 */
export function useModuleAccess(moduleId?: string | null) {
  const { hasPermission, hasFullAccess } = useRoleAccess();
  const routeModule = usePermissionModule();
  const module = moduleId || routeModule;

  return useMemo(() => {
    const can = (action: string) => {
      // Màn cá nhân / hướng dẫn: in tự do; xuất / nhập vẫn chỉ Admin
      if (module === PERMISSION_FREE_MODULE) return action === "print" || hasFullAccess;
      return module ? hasPermission(module, action) : hasFullAccess;
    };
    const name = module && module !== PERMISSION_FREE_MODULE ? permissionModuleName(module) : "màn này";
    return {
      module,
      moduleName: name,
      canPrint: can("print"),
      canExport: can("export"),
      canImport: can("import"),
    };
  }, [module, hasPermission, hasFullAccess]);
}

/** Chặn In khi chưa được tích quyền In ở module: trả hàm bọc handler in, tự báo lý do */
export function usePrintGuard(moduleId?: string | null) {
  const access = useModuleAccess(moduleId);
  return useCallback(
    <T extends (...args: any[]) => any>(fn: T) =>
      ((...args: any[]) => {
        if (!access.canPrint) {
          toast.error(`Bạn chưa có quyền In ở ${access.moduleName}. Liên hệ Admin để được cấp quyền trên bảng Phân quyền.`);
          return;
        }
        return fn(...args);
      }) as T,
    [access.canPrint, access.moduleName],
  );
}
